import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';

const API_BASE = import.meta.env.VITE_API_URL
  ? `https://${import.meta.env.VITE_API_URL}`
  : 'https://api.helpped.com';

const APP_STORE_URL = 'https://apps.apple.com/us/app/helpped/id6764492890';

// ── Real civic events pulled 2026-05-31 — shown until /api/activity responds ──

const FALLBACK_ACTIVITY: ActivityItem[] = [
  { title: 'Board of County Commissioners: Federal Homeland Security Grant Award — $726,158', city: 'Spokane County', level: 'county', event_type: 'council_vote', is_urgent: true,  starts_at: '2026-05-19T14:00:00Z' },
  { title: 'City Council: City Light Department — King County Easement Grant',                 city: 'Seattle',        level: 'city',   event_type: 'council_vote', is_urgent: true,  starts_at: '2026-06-02T14:00:00Z' },
  { title: 'City Council Regular Meeting: NE Spring Blvd Zone 3 — Professional Services Agreement', city: 'Bellevue', level: 'city',   event_type: 'council_vote', is_urgent: false, starts_at: '2026-06-02T18:00:00Z' },
  { title: 'City Council: Agreement with Berger Partnership for Park Construction Administration',   city: 'Olympia',  level: 'city',   event_type: 'council_vote', is_urgent: false, starts_at: '2026-06-02T18:00:00Z' },
  { title: 'Debt Management Commission: General Obligation Bonds for Clark County School District',  city: 'Clark County', level: 'county', event_type: 'council_vote', is_urgent: true, starts_at: '2026-06-04T09:00:00Z' },
  { title: 'Board of County Commissioners: Crestline Street Sidewalk Improvements — Liberty NW Contract', city: 'Spokane County', level: 'county', event_type: 'council_vote', is_urgent: true, starts_at: '2026-06-02T14:00:00Z' },
  { title: 'Regular City Council Meeting: Logan Ave S Temporary Full Closure May 26 – Oct 2', city: 'Renton',  level: 'city',   event_type: 'council_vote', is_urgent: false, starts_at: '2026-05-18T19:00:00Z' },
  { title: 'Board of County Commissioners: Fire Sprinkler & Alarm System — Patriot Fire Protection', city: 'Spokane County', level: 'county', event_type: 'council_vote', is_urgent: true, starts_at: '2026-06-02T14:00:00Z' },
  { title: 'Regular City Council: Amendment No. 3 to CAG-23-451 with Cascadia Consulting ($212,917)', city: 'Renton', level: 'city', event_type: 'council_vote', is_urgent: true, starts_at: '2026-05-18T19:00:00Z' },
  { title: 'Board of County Commissioners: Revise Traffic Code — All-Way Stop at Appleway & Wellesley', city: 'Spokane County', level: 'county', event_type: 'council_vote', is_urgent: false, starts_at: '2026-06-02T09:00:00Z' },
  { title: 'Debt Management Commission: General Obligation Bonds for Beltway Transportation', city: 'Clark County', level: 'county', event_type: 'council_vote', is_urgent: true, starts_at: '2026-06-04T09:00:00Z' },
  { title: 'Board of County Commissioners: MOU — Spokane County & Town of Fairfield Waste Reduction', city: 'Spokane County', level: 'county', event_type: 'council_vote', is_urgent: false, starts_at: '2026-05-19T14:00:00Z' },
];

// ── Known stats — shown immediately, replaced by live data when API responds ──

const FALLBACK_STATS: StatsData = {
  civic_events: 2198,
  upcoming_meetings: 551,
  places_indexed: 104287,
  state_bills: 221,
  jurisdictions_monitored: 19,
  coverage: [
    // Cities
    { name: 'Everett',    events: 527,  level: 'city' },
    { name: 'Renton',     events: 247,  level: 'city' },
    { name: 'Bellevue',   events: 130,  level: 'city' },
    { name: 'Olympia',    events: 127,  level: 'city' },
    { name: 'Auburn',     events: 83,   level: 'city' },
    { name: 'Redmond',    events: 79,   level: 'city' },
    { name: 'Seattle',    events: 74,   level: 'city' },
    { name: 'Tacoma',     events: 51,   level: 'city' },
    { name: 'Sammamish',  events: 22,   level: 'city' },
    { name: 'Kirkland',   events: 6,    level: 'city' },
    { name: 'Spokane',    events: 10,   level: 'city' },
    // Counties
    { name: 'Snohomish County', events: 185, level: 'county' },
    { name: 'Spokane County',   events: 168, level: 'county' },
    { name: 'Whatcom County',   events: 131, level: 'county' },
    { name: 'Clark County',     events: 72,  level: 'county' },
    { name: 'King County',      events: 60,  level: 'county' },
    { name: 'Pierce County',    events: 9,   level: 'county' },
    // State
    { name: 'WA Legislature', events: 221, level: 'state' },
  ],
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface CoverageCity {
  name: string;
  events: number;
  level: 'city' | 'county' | 'state';
}

interface StatsData {
  civic_events: number;
  upcoming_meetings: number;
  places_indexed: number;
  state_bills: number;
  jurisdictions_monitored: number;
  coverage: CoverageCity[];
}

interface ActivityItem {
  title: string;
  city: string;
  level: string;
  event_type: string;
  is_urgent: boolean;
  starts_at: string | null;
}


// ── Count-up hook ─────────────────────────────────────────────────────────────

function useCountUp(target: number, duration = 1800, active = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active || target === 0) return;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(eased * target));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [target, duration, active]);
  return value;
}

// ── Relative time helper ──────────────────────────────────────────────────────

function relTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = Date.now();
  const diff = d.getTime() - now;
  const abs = Math.abs(diff);
  if (abs < 3600_000) return 'today';
  if (abs < 86_400_000) {
    const h = Math.round(abs / 3600_000);
    return diff > 0 ? `in ${h}h` : `${h}h ago`;
  }
  const days = Math.round(abs / 86_400_000);
  return diff > 0 ? `in ${days}d` : `${days}d ago`;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return n.toLocaleString();
  return String(n);
}

// ── Stat counter card ─────────────────────────────────────────────────────────

function StatCard({
  value,
  suffix = '',
  label,
  sub,
  active,
}: {
  value: number;
  suffix?: string;
  label: string;
  sub?: string;
  active: boolean;
}) {
  const count = useCountUp(value, 1800, active);
  return (
    <div className="flex flex-col items-center text-center px-4 py-6">
      <span className="text-4xl sm:text-5xl font-extrabold text-gray-900 tabular-nums leading-none">
        {formatNumber(count)}{suffix}
      </span>
      <span className="mt-2 text-sm font-semibold text-gray-700">{label}</span>
      {sub && <span className="text-xs text-gray-400 mt-0.5">{sub}</span>}
    </div>
  );
}

// ── Activity feed item ────────────────────────────────────────────────────────

function ActivityRow({ item }: { item: ActivityItem }) {
  const typeLabel =
    item.event_type === 'state_bill'
      ? 'State Bill'
      : item.event_type === 'council_meeting'
      ? 'Council Meeting'
      : 'Council Vote';

  return (
    <div className="flex items-start gap-3 py-3 px-4 border-b border-gray-100 last:border-0">
      <div className="mt-0.5 shrink-0">
        {item.is_urgent ? (
          <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            Urgent
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
            {typeLabel}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800 leading-snug line-clamp-2">{item.title}</p>
        <div className="flex items-center gap-2 mt-1">
          <span
            className="text-xs font-semibold px-1.5 py-0.5 rounded"
            style={{ background: 'hsl(158 68% 93%)', color: 'hsl(158 68% 28%)' }}
          >
            {item.city}
          </span>
          {item.starts_at && (
            <span className="text-xs text-gray-400">{relTime(item.starts_at)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Home() {
  usePageMeta(
    'Helpped — Washington State Civic & Neighborhood App',
    'Track every city council vote, public meeting, and neighborhood update across Washington — Seattle, Tacoma, Bellevue, Spokane, Everett, Olympia, and 13+ more cities. Free on iOS.',
  );

  const [stats, setStats] = useState<StatsData>(FALLBACK_STATS);
  const [activity, setActivity] = useState<ActivityItem[]>(FALLBACK_ACTIVITY);
  const [statsActive, setStatsActive] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  // Fetch live stats + activity — fallback values shown immediately
  useEffect(() => {
    fetch(`${API_BASE}/api/stats`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setStats(d))
      .catch(() => {});

    fetch(`${API_BASE}/api/activity`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (Array.isArray(d) && d.length > 0) setActivity(d); })
      .catch(() => {});
  }, []);

  // Trigger count-up animation when stats section scrolls into view
  useEffect(() => {
    if (!statsRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsActive(true); },
      { threshold: 0.3 },
    );
    obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  const cities = stats.coverage.filter((c) => c.level === 'city');
  const counties = stats.coverage.filter((c) => c.level === 'county');
  const state = stats.coverage.filter((c) => c.level === 'state');

  return (
    <>
      {/* Structured data — Organization + WebSite + MobileApplication */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              '@context': 'https://schema.org',
              '@type': 'Organization',
              '@id': 'https://helpped.com/#organization',
              name: 'Helpped',
              url: 'https://helpped.com',
              logo: 'https://helpped.com/logo512.png',
              contactPoint: {
                '@type': 'ContactPoint',
                email: 'hello@helpped.com',
                contactType: 'customer support',
              },
              areaServed: {
                '@type': 'State',
                name: 'Washington',
                addressCountry: 'US',
              },
              sameAs: [],
            },
            {
              '@context': 'https://schema.org',
              '@type': 'WebSite',
              '@id': 'https://helpped.com/#website',
              url: 'https://helpped.com',
              name: 'Helpped',
              description:
                'Washington State civic and neighborhood app. Track every city council vote, public meeting, and neighborhood update across Washington.',
              publisher: { '@id': 'https://helpped.com/#organization' },
            },
            {
              '@context': 'https://schema.org',
              '@type': 'MobileApplication',
              name: 'Helpped',
              operatingSystem: 'iOS',
              applicationCategory: 'NewsApplication',
              offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
              description:
                'Track every Washington State city council vote, public meeting, and neighborhood gem. Coverage across Seattle, Tacoma, Bellevue, Spokane, Everett, Olympia, and 13+ more cities.',
              publisher: { '@id': 'https://helpped.com/#organization' },
            },
          ]),
        }}
      />

      <div className="min-h-screen flex flex-col bg-white">

        {/* ── Nav ── */}
        <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                style={{ background: 'hsl(var(--primary))' }}
              >
                H
              </div>
              <span className="font-bold text-gray-900 tracking-tight">Helpped</span>
            </div>
            <div className="flex items-center gap-5">
              <a
                href="#coverage"
                className="hidden sm:block text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
              >
                Coverage
              </a>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-white px-4 py-1.5 rounded-full transition-opacity hover:opacity-90"
                style={{ background: 'hsl(var(--primary))' }}
              >
                Get the app
              </a>
            </div>
          </div>
        </nav>

        {/* ── Hero ── */}
        <section className="max-w-6xl mx-auto px-6 pt-16 pb-12 grid lg:grid-cols-2 gap-10 items-start">
          {/* Left */}
          <div>
            <div
              className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-6 border"
              style={{
                background: 'hsl(158 68% 95%)',
                borderColor: 'hsl(158 68% 80%)',
                color: 'hsl(158 68% 28%)',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
              Live across Washington State
            </div>

            <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight mb-5">
              Every council vote.<br />
              Every neighborhood gem.<br />
              <span style={{ color: 'hsl(var(--primary))' }}>One app.</span>
            </h1>

            <p className="text-lg text-gray-500 leading-relaxed mb-8 max-w-lg">
              Helpped monitors{' '}
              {stats ? stats.jurisdictions_monitored : '19'}{' '}
              Washington jurisdictions — from the State Legislature to your city council
              — and surfaces what matters on your block.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl font-semibold text-white shadow-lg transition-transform hover:scale-105 active:scale-95 text-sm"
                style={{ background: '#000' }}
              >
                <svg width="18" height="22" viewBox="0 0 814 1000" fill="white">
                  <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 384.7 8.1 191.3 8.1 106.2c0-134.9 87.6-206.1 173.4-206.1 46.9 0 85.9 30.7 114.8 30.7 27.5 0 71.5-32.7 123.1-32.7 20.8 0 108.2 1.9 160.8 67.1zm-138.9-141.9C617.1 138.5 651.9 89 651.9 39.5c0-6.4-.7-12.8-1.3-19.3-47.3 1.9-101.7 31.3-134.2 71.6-29.4 35.2-57.8 86.4-57.8 135.7 0 6.4.7 12.8 1.3 19.3 4.5.7 9 1.3 13.5 1.3 44.2 0 95.9-27.5 127.8-69.8z" />
                </svg>
                Download on the App Store
              </a>
            </div>
          </div>

          {/* Right — live activity feed */}
          <div className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden bg-white">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Live across WA
                </span>
              </div>
              <span className="text-xs text-gray-400">{activity.length} recent updates</span>
            </div>
            <div className="divide-y divide-gray-50 max-h-[420px] overflow-y-auto">
              {activity.map((item, i) => <ActivityRow key={i} item={item} />)}
            </div>
          </div>
        </section>

        {/* ── Stat counters ── */}
        <section
          ref={statsRef}
          className="border-y border-gray-100"
          style={{ background: 'hsl(158 68% 97%)' }}
        >
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-gray-200">
              <StatCard
                value={stats?.civic_events ?? 0}
                label="Civic events tracked"
                sub="votes, meetings, bills"
                active={statsActive}
              />
              <StatCard
                value={stats?.upcoming_meetings ?? 0}
                label="Upcoming public meetings"
                sub="open to attend"
                active={statsActive}
              />
              <StatCard
                value={stats?.places_indexed ?? 0}
                label="Local places mapped"
                sub="across WA neighborhoods"
                active={statsActive}
              />
              <StatCard
                value={stats?.state_bills ?? 0}
                label="WA state bills tracked"
                sub="housing, health & more"
                active={statsActive}
              />
            </div>
            <p className="text-center text-xs text-gray-400 pb-3">Updated daily from live government sources</p>
          </div>
        </section>

        {/* ── Mid-page CTA — after stats, highest intent moment ── */}
        <div className="px-6 py-5" style={{ background: 'hsl(30 10% 94%)' }}>
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm font-medium text-gray-700">
              See all of this in your neighborhood's feed —{' '}
              <span className="font-semibold text-gray-900">free on iOS.</span>
            </p>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white text-sm transition-opacity hover:opacity-90"
              style={{ background: '#000' }}
            >
              <svg width="14" height="17" viewBox="0 0 814 1000" fill="white">
                <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 384.7 8.1 191.3 8.1 106.2c0-134.9 87.6-206.1 173.4-206.1 46.9 0 85.9 30.7 114.8 30.7 27.5 0 71.5-32.7 123.1-32.7 20.8 0 108.2 1.9 160.8 67.1zm-138.9-141.9C617.1 138.5 651.9 89 651.9 39.5c0-6.4-.7-12.8-1.3-19.3-47.3 1.9-101.7 31.3-134.2 71.6-29.4 35.2-57.8 86.4-57.8 135.7 0 6.4.7 12.8 1.3 19.3 4.5.7 9 1.3 13.5 1.3 44.2 0 95.9-27.5 127.8-69.8z"/>
              </svg>
              Get Helpped on iOS
            </a>
          </div>
        </div>

        {/* ── Washington coverage ── */}
        <section id="coverage" className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Washington, tracked block by block
            </h2>
            <p className="text-gray-500 text-sm max-w-xl mx-auto">
              Real-time civic intelligence from{' '}
              {stats?.jurisdictions_monitored ?? '19'} jurisdictions — city councils, county
              boards, and the State Legislature.
            </p>
          </div>

          {/* Cities */}
          {cities.length > 0 && (
            <div className="mb-8">
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
                Cities
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {cities.map((c) => (
                  <CoverageCard key={c.name} city={c} />
                ))}
              </div>
            </div>
          )}

          {/* Counties */}
          {counties.length > 0 && (
            <div className="mb-8">
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
                Counties
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {counties.map((c) => (
                  <CoverageCard key={c.name} city={c} />
                ))}
              </div>
            </div>
          )}

          {/* State */}
          {state.length > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
                State
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {state.map((c) => (
                  <CoverageCard key={c.name} city={c} />
                ))}
              </div>
            </div>
          )}

        </section>

        {/* ── Features ── */}
        <section className="border-t border-gray-100 py-16 px-6" style={{ background: 'hsl(30 20% 98%)' }}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">
              Everything your neighborhood needs to know
            </h2>
            <div className="grid sm:grid-cols-3 gap-5">
              {[
                {
                  icon: '🏛️',
                  title: 'Civic pulse',
                  desc: 'Every council vote, public hearing, and state bill — plain-language summaries delivered to your block.',
                },
                {
                  icon: '📍',
                  title: 'Neighborhood map',
                  desc: 'Gems, free stuff, events, and alerts pinned to your exact street. See what your neighbors are sharing.',
                },
                {
                  icon: '🤖',
                  title: 'AI neighborhood guide',
                  desc: "Ask anything about your city and get answers grounded in what's actually happening near you.",
                },
              ].map((f) => (
                <div
                  key={f.title}
                  className="flex flex-col gap-3 p-5 rounded-2xl border bg-white shadow-sm"
                  style={{ borderColor: 'hsl(var(--border))' }}
                >
                  <span className="text-3xl">{f.icon}</span>
                  <h3 className="font-semibold text-gray-900">{f.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="max-w-3xl mx-auto px-6 py-16 w-full">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-10 text-center">
            Frequently asked questions
          </h2>
          <div className="space-y-8">
            <FaqItem question="What is Helpped?">
              Helpped is a free iOS app that brings Washington State civic news and neighborhood activity to your phone. It tracks every city council vote, public meeting, and state legislature bill across 19 WA jurisdictions — then delivers the items relevant to your specific block. Instead of visiting a dozen government websites, you get a single feed tailored to where you live.
            </FaqItem>
            <FaqItem question="Which Washington cities and counties does Helpped cover?">
              Helpped currently monitors city councils in Seattle, Tacoma, Bellevue, Renton, Everett, Kirkland, Redmond, Sammamish, Olympia, Auburn, and Spokane, plus county boards for King, Pierce, Snohomish, Clark, Whatcom, and Spokane County. It also tracks the Washington State Legislature for housing, health, and social services bills. More jurisdictions are added regularly — over 2,100 civic events have been indexed so far.
            </FaqItem>
            <FaqItem question="How does Helpped track city council votes?">
              Helpped connects directly to government meeting platforms — Legistar (used by Seattle, Bellevue, Tacoma, King County, and others), CivicClerk (Auburn, Spokane County), PrimeGov (Renton, Kirkland), CivicPlus (Everett), and the Washington State Legislature API. It pulls agenda items and vote records as they are published, converts them to plain-language summaries, and flags urgent items that affect your neighborhood.
            </FaqItem>
            <FaqItem question="Is Helpped free?">
              Yes, Helpped is completely free to download and use. There are no subscriptions, paywalls, or in-app purchases. Download it free on the App Store for iPhone.
            </FaqItem>
            <FaqItem question="What is the Washington State Legislature civic feed?">
              The WA Legislature feed inside Helpped tracks active state bills focused on housing, homelessness, mental health, food access, and social services — the categories most likely to affect your block. Each bill gets a plain-language summary so you can understand what is being voted on without reading legal text. Helpped currently tracks 221 active state bills.
            </FaqItem>
            <FaqItem question="How do I follow my city council in Washington?">
              Download Helpped free on the App Store, open the app, and allow location access. Helpped automatically detects your city and begins showing council votes, upcoming public meetings, and neighborhood posts relevant to your area. You can also attend or RSVP to upcoming public meetings directly from the app.
            </FaqItem>
            <FaqItem question="What are neighborhood gems?">
              Gems are local tips posted by residents — a hidden park, a great coffee spot, a free community resource, or anything worth knowing about your block. They appear as pins on the neighborhood map alongside civic events and neighbor posts. Helpped has indexed over 104,000 local places across Washington to give gems useful context.
            </FaqItem>
          </div>
        </section>

        {/* ── App Store CTA ── */}
        <section className="px-6 py-16" style={{ background: 'hsl(158 68% 95%)' }}>
          <div className="max-w-md mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Available now on iOS</h2>
            <p className="text-gray-600 mb-8 text-sm">
              Download Helpped free on the App Store and start following your neighborhood today.
            </p>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-semibold text-white shadow-lg transition-transform hover:scale-105 active:scale-95 text-sm"
              style={{ background: '#000' }}
            >
              <svg width="20" height="24" viewBox="0 0 814 1000" fill="white">
                <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 384.7 8.1 191.3 8.1 106.2c0-134.9 87.6-206.1 173.4-206.1 46.9 0 85.9 30.7 114.8 30.7 27.5 0 71.5-32.7 123.1-32.7 20.8 0 108.2 1.9 160.8 67.1zm-138.9-141.9C617.1 138.5 651.9 89 651.9 39.5c0-6.4-.7-12.8-1.3-19.3-47.3 1.9-101.7 31.3-134.2 71.6-29.4 35.2-57.8 86.4-57.8 135.7 0 6.4.7 12.8 1.3 19.3 4.5.7 9 1.3 13.5 1.3 44.2 0 95.9-27.5 127.8-69.8z" />
              </svg>
              Download on the App Store
            </a>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer className="px-6 py-8 border-t border-gray-100">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
            <span>© {new Date().getFullYear()} Helpped — Washington State</span>
            <div className="flex gap-6">
              <Link to="/privacy" className="hover:text-gray-600 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-gray-600 transition-colors">Terms</Link>
              <a href="mailto:hello@helpped.com" className="hover:text-gray-600 transition-colors">Contact</a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

// ── FAQ item ──────────────────────────────────────────────────────────────────

function FaqItem({ question, children }: { question: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-gray-100 pb-8">
      <h3 className="text-base font-semibold text-gray-900 mb-3">{question}</h3>
      <p className="text-sm text-gray-600 leading-relaxed">{children}</p>
    </div>
  );
}

// ── Coverage card ─────────────────────────────────────────────────────────────

// Slugs that have a dedicated city landing page
const CITY_SLUGS: Record<string, string> = {
  'Everett': 'everett', 'Renton': 'renton', 'Bellevue': 'bellevue',
  'Olympia': 'olympia', 'Auburn': 'auburn', 'Redmond': 'redmond',
  'Seattle': 'seattle', 'Tacoma': 'tacoma', 'Kirkland': 'kirkland',
  'Sammamish': 'sammamish', 'Spokane': 'spokane',
};

function CoverageCard({ city }: { city: CoverageCity }) {
  const levelColor =
    city.level === 'state'
      ? { bg: 'hsl(271 70% 95%)', text: 'hsl(271 70% 40%)' }
      : city.level === 'county'
      ? { bg: 'hsl(38 90% 94%)', text: 'hsl(38 60% 38%)' }
      : { bg: 'hsl(158 68% 94%)', text: 'hsl(158 68% 28%)' };

  const slug = CITY_SLUGS[city.name];
  const inner = (
    <>
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: levelColor.text }} />
        <span className="text-sm font-medium text-gray-800 truncate">{city.name}</span>
      </div>
      <span className="text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ml-2"
        style={{ background: levelColor.bg, color: levelColor.text }}>
        {city.events.toLocaleString()}
      </span>
    </>
  );

  const baseClass = "flex items-center justify-between px-4 py-3 rounded-xl border border-gray-100 bg-white shadow-sm transition-shadow";

  return slug ? (
    <Link to={`/cities/${slug}`} className={`${baseClass} hover:shadow-md`}>
      {inner}
    </Link>
  ) : (
    <div className={baseClass}>{inner}</div>
  );
}
